import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { ProspectEvent } from "../../../services/v2/prospects.service";
import { diaClave, diaEtiqueta, fechaDia, fechaLarga, hora, KIND_LABEL } from "./salesHelpers";

interface Props {
	campusId: number;
	prospectId: number;
	events: ProspectEvent[];      // del más viejo al más nuevo
	hayMas: boolean;
	onMas: () => void;
	cargando: boolean;
}

const TICKS: { [k: string]: { t: string; label: string } } = {
	enviado: { t: "✓", label: "Enviado" }, entregado: { t: "✓✓", label: "Entregado" },
	leido: { t: "✓✓", label: "Leído" }, fallido: { t: "!", label: "No se entregó" },
};

/** Foto, audio o documento de WhatsApp: el archivo vive en Spaces privado y se pide una URL temporal al abrirlo. */
const Adjunto: React.FC<{ campusId: number; prospectId: number; ev: ProspectEvent }> = ({ campusId, prospectId, ev }) => {
	const [url, setUrl] = useState<string | null>(null);
	const [err, setErr] = useState<string | null>(null);
	const m = ev.meta || {};
	const tipo = String(m.type || "");
	if (m.url) {   // lo que mandó Woztell: link público de su almacenamiento
		return <a className="cv-adjunto" href={m.url} target="_blank" rel="noreferrer">{m.filename || "Abrir archivo"}</a>;
	}
	if (!m.file) {
		return m.woztell_file ? <span className="cv-adjunto apagado">Archivo guardado en Woztell (no disponible)</span> : null;
	}
	const abrir = async () => {
		try {
			const r = await HttpManager.getInstance().prospectsService.media(campusId, prospectId, ev.id);
			r.data.url ? setUrl(r.data.url) : setErr("No disponible");
		} catch (e) { setErr("No se pudo abrir"); }
	};
	if (!url) {
		return <button className="cv-adjunto" onClick={abrir}>{err || (tipo === "audio" ? "Escuchar audio" : tipo === "image" || tipo === "sticker" ? "Ver foto" : tipo === "video" ? "Ver video" : "Abrir documento")}</button>;
	}
	if (tipo === "image" || tipo === "sticker") return <a href={url} target="_blank" rel="noreferrer"><img className="cv-foto" src={url} alt="Foto que mandó" /></a>;
	if (tipo === "audio") return <audio className="cv-audio" controls autoPlay src={url} />;
	if (tipo === "video") return <video className="cv-foto" controls src={url} />;
	return <a className="cv-adjunto" href={url} target="_blank" rel="noreferrer">{m.filename || "Abrir documento"}</a>;
};

/**
 * La conversación (lienzo del 19-sep): WhatsApp en burbujas y, en la misma línea de tiempo, las notas, llamadas,
 * tareas y movimientos. Lo más nuevo abajo, como en WhatsApp.
 */
const Conversacion: React.FC<Props> = ({ campusId, prospectId, events, hayMas, onMas, cargando }) => {
	const caja = useRef<HTMLDivElement>(null);
	const alto = useRef<number>(0);
	const primero = useRef<number | null>(null);

	// Al abrir o al llegar algo nuevo: hasta abajo. Al cargar anteriores: se queda donde estaba.
	useLayoutEffect(() => {
		const el = caja.current;
		if (!el) return;
		const primerId = events.length ? events[0].id : null;
		if (primero.current !== null && primerId !== primero.current && alto.current) {
			el.scrollTop = el.scrollHeight - alto.current;
		} else {
			el.scrollTop = el.scrollHeight;
		}
		primero.current = primerId;
		alto.current = el.scrollHeight;
	}, [events]);

	useEffect(() => { primero.current = null; alto.current = 0; }, [prospectId]);

	let ultimoDia = -1;
	return (
		<div className="cv-chat" ref={caja}>
			{hayMas ? <div className="cv-mas"><button className="btn-wa" disabled={cargando} onClick={onMas}>{cargando ? "Cargando…" : "Ver anteriores"}</button></div> : null}
			{!events.length && !cargando ? <div className="ventas-vacio">Sin mensajes ni notas todavía.</div> : null}
			{events.map((ev) => {
				const dia = diaClave(ev.occurred_at);
				const sep = dia !== ultimoDia ? <div className="cv-dia" key={"d" + ev.id}>{diaEtiqueta(ev.occurred_at)}</div> : null;
				ultimoDia = dia;
				if (ev.kind === "whatsapp") {
					const sale = ev.direction === "out";
					const tick = sale && ev.wa_status ? TICKS[ev.wa_status] : null;
					return (
						<React.Fragment key={ev.id}>
							{sep}
							<div className={"cv-burbuja " + (sale ? "sale" : "entra") + (ev.wa_status === "fallido" ? " fallido" : "")} title={fechaLarga(ev.occurred_at)}>
								{ev.meta?.template ? <div className="cv-plantilla">Plantilla · {ev.meta.template}</div> : null}
								{ev.body ? <p>{ev.body}</p> : null}
								<Adjunto campusId={campusId} prospectId={prospectId} ev={ev} />
								<span className="cv-pie">
									{hora(ev.occurred_at)}{sale && ev.author_name ? " · " + ev.author_name.split(" ")[0] : ""}
									{tick ? <span className={"cv-tick " + ev.wa_status} title={tick.label} aria-label={tick.label}> {tick.t}</span> : null}
								</span>
								{ev.wa_status === "fallido" && ev.meta?.error ? <span className="cv-error">No se entregó: {ev.meta.error}</span> : null}
							</div>
						</React.Fragment>
					);
				}
				if (ev.kind === "nota" || ev.kind === "llamada" || ev.kind === "tarea") {
					return (
						<React.Fragment key={ev.id}>
							{sep}
							<div className={"cv-tarjeta " + ev.kind}>
								<div className="cv-tarjeta-cab">
									<span className="t">{KIND_LABEL[ev.kind]}{ev.kind === "llamada" && ev.direction === "in" ? " entrante" : ""}</span>
									<span className="meta">{hora(ev.occurred_at)}{ev.author_name ? " · " + ev.author_name : ""}</span>
								</div>
								{ev.body ? <p>{ev.body}</p> : null}
								{ev.kind === "tarea" && ev.meta?.status ? <div className="meta">{ev.meta.status}{ev.meta.due ? " · vence " + fechaDia(ev.meta.due) : ""}</div> : null}
							</div>
						</React.Fragment>
					);
				}
				return (
					<React.Fragment key={ev.id}>
						{sep}
						<div className={"cv-movimiento " + ev.kind}>
							<span>{ev.body}</span>
							<span className="meta">{hora(ev.occurred_at)}{ev.author_name ? " · " + ev.author_name : ""}</span>
						</div>
					</React.Fragment>
				);
			})}
		</div>
	);
};

export default Conversacion;
